import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  FolderOpen, Plus, Eye, Edit, Clock, CheckCircle, 
  AlertCircle, Package, ChevronRight
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  draft: { label: 'Borrador', color: 'bg-slate-500', icon: Edit },
  pending_review: { label: 'Pendiente', color: 'bg-yellow-500', icon: Clock },
  approved: { label: 'Aprobado', color: 'bg-green-500', icon: CheckCircle },
  in_production: { label: 'En Producción', color: 'bg-blue-500', icon: Package },
  completed: { label: 'Completado', color: 'bg-emerald-500', icon: CheckCircle },
  rejected: { label: 'Rechazado', color: 'bg-red-500', icon: AlertCircle }
};

export default function MyProjectsPage() {
  const { t } = useTranslation();
  const { user, token, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/auth', { state: { from: { pathname: '/mis-proyectos' } } });
      return;
    }
    fetchProjects();
  }, [isAuthenticated, navigate]);

  const fetchProjects = async () => {
    try {
      const response = await fetch(`${API_URL}/api/my-projects`, {
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setProjects(data);
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-6">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white">{t('nav.myProjects')}</h1>
            <p className="text-slate-400 mt-1">
              Hola, {user?.name}. Gestiona tus proyectos de bebidas.
            </p>
          </div>
          <Link to="/configurador">
            <Button className="bg-amber-600 hover:bg-amber-700" data-testid="new-project-btn">
              <Plus className="w-4 h-4 mr-2" />
              Nuevo Proyecto
            </Button>
          </Link>
        </div>

        {/* Projects List */}
        {projects.length === 0 ? (
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-12 text-center">
              <FolderOpen className="w-16 h-16 text-slate-500 mx-auto mb-4" />
              <h3 className="text-xl text-white mb-2">No tienes proyectos todavía</h3>
              <p className="text-slate-400 mb-6">
                Comienza a diseñar tu primera bebida con nuestro configurador industrial
              </p>
              <Link to="/configurador">
                <Button className="bg-amber-600 hover:bg-amber-700">
                  <Plus className="w-4 h-4 mr-2" />
                  Crear Primer Proyecto
                </Button>
              </Link>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {projects.map(project => {
              const status = STATUS_CONFIG[project.status] || STATUS_CONFIG.draft;
              const StatusIcon = status.icon;
              
              return (
                <Card 
                  key={project.id} 
                  className="bg-slate-800/50 border-slate-700 hover:border-amber-500/50 transition-all"
                  data-testid={`project-card-${project.id}`}
                >
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-lg font-semibold text-white">
                            {project.brand?.brand_name || project.beverage?.category || 'Proyecto sin nombre'}
                          </h3>
                          <Badge className={`${status.color} text-white`}>
                            <StatusIcon className="w-3 h-3 mr-1" />
                            {status.label}
                          </Badge>
                        </div>
                        
                        <div className="flex flex-wrap gap-4 text-sm text-slate-400">
                          {project.beverage?.category && (
                            <span className="flex items-center">
                              <span className="w-2 h-2 rounded-full bg-amber-500 mr-2" />
                              {project.beverage.category}
                            </span>
                          )}
                          {project.packaging?.container_type && (
                            <span className="flex items-center">
                              <Package className="w-4 h-4 mr-1" />
                              {project.packaging.container_type}
                            </span>
                          )}
                          {project.production?.total_volume && (
                            <span>Vol: {project.production.total_volume}</span>
                          )}
                        </div>

                        <p className="text-xs text-slate-500 mt-3">
                          Creado: {new Date(project.created_at).toLocaleDateString()}
                          {project.updated_at !== project.created_at && (
                            <span> · Actualizado: {new Date(project.updated_at).toLocaleDateString()}</span>
                          )}
                        </p>
                      </div>

                      <div className="flex items-center gap-2">
                        {project.status === 'draft' ? (
                          <Link to={`/configurador?project=${project.id}`}>
                            <Button variant="outline" size="sm" className="border-slate-600 text-white hover:bg-slate-700">
                              <Edit className="w-4 h-4 mr-1" />
                              Continuar
                            </Button>
                          </Link>
                        ) : (
                          <Button variant="outline" size="sm" className="border-slate-600 text-white hover:bg-slate-700">
                            <Eye className="w-4 h-4 mr-1" />
                            Ver
                          </Button>
                        )}
                        <ChevronRight className="w-5 h-5 text-slate-500" />
                      </div>
                    </div>

                    {/* Warnings */}
                    {project.warnings && project.warnings.length > 0 && (
                      <div className="mt-4 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                        <p className="text-sm text-amber-400 flex items-center">
                          <AlertCircle className="w-4 h-4 mr-2" />
                          {project.warnings.length} advertencia(s) de compatibilidad
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
